'use client'
import React, { useState, useEffect } from 'react'
import SectionTitle from "@/components/ui/SectionTitle";
import TourCard, { TourProps } from "@/components/ui/TourCard";
import TourCardSkeleton from "@/components/ui/TourCardSkeleton";
import Link from "next/link";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import type { PageUrlMap } from "@/types/blog";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";
import { resolveUrlFromMap } from "@/lib/blog-transformers";

interface HomeFeaturedToursProps {
    languageId?: number;
    language?: string;
    languageCode?: string;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    /** API'den / resolveProps'tan gelen turlar (kategori seçiliyse) */
    initialTours?: TourProps[];
    /** Bölüm başlığı (trait'ten) */
    title?: string;
    /** Bölüm alt başlığı / küçük başlık (trait'ten) */
    subTitle?: string;
    /** VIEW ALL TOUR link URL (trait'ten) */
    viewAllUrl?: string;
    /** Seçili kategori ID'leri (trait'ten kaydedilir; backend hydration / resolveProps ile turlar doldurulur) */
    categoryIds?: number[];
    /** Gösterilecek tur sayısı (trait panelden; backend/resolveProps tek istekte kullanır) */
    limit?: number;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
    };
}

const DEFAULT_TITLE = 'Amazing Featured Tour Package the world';
const DEFAULT_SUB_TITLE = 'Explore the world';

export default function HomeFeaturedTours(props?: HomeFeaturedToursProps) {
    const {
        languageId: propLanguageId,
        language: propLanguage,
        languageCode: propLanguageCode,
        pageUrls,
        defaultLanguage = 'en',
        initialTours: propInitialTours,
        title: propTitle,
        subTitle: propSubTitle,
        viewAllUrl: propViewAllUrl,
        categoryIds: propCategoryIds,
        limit: propLimit,
        __pageContext
    } = props || {};
    
    const { getVariable } = useLanguageVariables();
    const languageId = propLanguageId || __pageContext?.languageId;
    const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;

    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
    const [componentProps, setComponentProps] = useState<{ title?: string; subTitle?: string; viewAllUrl?: string; limit?: number }>({});

    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const el = typeof document !== 'undefined' ? document.querySelector('[data-component-path="sections/HomeFeaturedTours"]') : null;
        const raw = el?.getAttribute?.('data-component-props');
        if (raw) {
            try {
                const parsed = JSON.parse(raw) as Record<string, unknown>;
                if (parsed && typeof parsed === 'object') {
                    if (parsed.title != null) setComponentProps(prev => ({ ...prev, title: String(parsed.title) }));
                    if (parsed.subTitle != null) setComponentProps(prev => ({ ...prev, subTitle: String(parsed.subTitle) }));
                    if (parsed.viewAllUrl != null) setComponentProps(prev => ({ ...prev, viewAllUrl: String(parsed.viewAllUrl) }));
                    if (parsed.limit != null) setComponentProps(prev => ({ ...prev, limit: Number(parsed.limit) || 6 }));
                }
            } catch { /* ignore */ }
        }
    }, []);

    const displayTitle = propTitle ?? componentProps.title ?? DEFAULT_TITLE;
    const displaySubTitle = propSubTitle ?? componentProps.subTitle ?? DEFAULT_SUB_TITLE;
    const rawViewAllUrl = (propViewAllUrl ?? componentProps.viewAllUrl ?? '').trim();
    const toursListingFallback = resolveUrlFromMap(
        pageUrls,
        'private-tours',
        resolvedLanguageCode || defaultLanguage || 'en',
        defaultLanguage || 'en',
        '/private-tours',
    );
    const displayViewAllUrl =
        rawViewAllUrl && rawViewAllUrl !== '#' ? rawViewAllUrl : (toursListingFallback || '/private-tours');
    const displayLimit = Math.min(20, Math.max(1, propLimit ?? componentProps.limit ?? 6));
    const categoryIds = Array.isArray(propCategoryIds) && propCategoryIds.length > 0 ? propCategoryIds : [];
    const hasServerData = propInitialTours !== undefined && Array.isArray(propInitialTours);
    const serverTours = hasServerData ? propInitialTours : null;
    /** Backend hydration / resolveProps henüz doldurmadıysa; categoryIds varsa skeleton göster (ek client isteği yok). */
    const isLoading = categoryIds.length > 0 && !hasServerData;

    const featuredToursFallback: TourProps[] = [
        {
            src: [
                'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1541445130327-1a4b0b6e6a79?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1566552881560-0be862a7c445?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1598441406699-71a6e0a58e1f?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Cappadocia Hot Air Balloons at Sunrise',
                'Cappadocia Cave Hotels and Fairy Chimneys',
                'Göreme Open Air Museum',
                'Underground City Exploration',
                'Traditional Turkish Pottery Workshop in Avanos'
            ],
            type: 'Group Tour',
            imageCount: 45,
            videoCount: 3,
            title: 'Magical Cappadocia - Hot Air Balloon & Cave Hotels',
            transferType: 'Private Transfer',
            start: 'İstanbul',
            stops: 'Göreme, Ürgüp, Avanos, Paşabağ',
            end: 'İstanbul',
            tourDuration: '3 Days',
            tag: 'Group',
            price: {
                discount_price: '$289.00',
                price: '$349.00',
            },
            reviews: 24
        },
        {
            src: [
                'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1533104816931-20fa691ff6ca?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1601581987809-a874a81309c9?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Luxury Yacht sailing Greek Islands',
                'Santorini White Houses and Blue Domes',
                'Mykonos Windmills at Sunset',
                'Paros Island Beach and Crystal Waters',
                'Naxos Ancient Portara Temple'
            ],
            type: 'Private Tour',
            imageCount: 52,
            videoCount: 5,
            title: 'Private Greek Islands - Santorini & Mykonos Yacht Experience',
            transferType: 'Yacht',
            start: 'Athens',
            stops: 'Santorini, Mykonos, Paros, Naxos',
            end: 'Athens',
            tourDuration: '7 Days',
            tag: 'Private',
            price: {
                discount_price: '$1,899.00',
                price: '$2,299.00',
            },
            reviews: 18
        },
        {
            src: [
                'https://images.unsplash.com/photo-1512100356356-de1b84283e18?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1550340499-a6c60fc8287c?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1529260830199-42c24126f198?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Tuscany Vineyard Landscape',
                'Florence Cathedral Duomo',
                'Siena Medieval Piazza del Campo',
                'Pisa Leaning Tower',
                'Cinque Terre Colorful Village'
            ],
            type: 'Group Tour',
            imageCount: 38,
            videoCount: 2,
            title: 'Italian Culinary Journey - Tuscany Wine & Food Tour',
            transferType: 'Bus',
            start: 'Rome',
            stops: 'Florence, Siena, Pisa, Cinque Terre',
            end: 'Milan',
            tourDuration: '5 Days',
            tag: 'Group',
            price: {
                discount_price: '$679.00',
                price: '$799.00',
            },
            reviews: 31
        },
        {
            src: [
                'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1579033461380-adb47c3eb938?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Northern Lights Aurora Borealis over Mountains',
                'Bergen Colorful Hanseatic Wharf Bryggen',
                'Tromsø Arctic Cathedral',
                'Lofoten Islands Dramatic Peaks',
                'Norwegian Fjords Scenic View'
            ],
            type: 'Group Tour',
            imageCount: 41,
            videoCount: 4,
            title: 'Arctic Adventure - Northern Lights & Fjords of Norway',
            transferType: 'Private Transfer',
            start: 'Oslo',
            stops: 'Bergen, Tromsø, Lofoten Islands',
            end: 'Oslo',
            tourDuration: '6 Days',
            tag: 'Group',
            price: {
                discount_price: '$1,249.00',
                price: '$1,499.00',
            },
            reviews: 15
        },
        {
            src: [
                'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1478436127897-769e1b3f0f36?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Tokyo Shibuya Crossing at Night',
                'Kyoto Cherry Blossoms and Traditional Temple',
                'Mount Fuji with Cherry Blossoms',
                'Osaka Castle in Spring',
                'Nara Deer Park and Temple'
            ],
            type: 'Private Tour',
            imageCount: 67,
            videoCount: 6,
            title: 'Cherry Blossom Season - Tokyo, Kyoto & Mount Fuji',
            transferType: 'High-Speed Train',
            start: 'Tokyo',
            stops: 'Hakone, Kyoto, Osaka, Nara',
            end: 'Tokyo',
            tourDuration: '8 Days',
            tag: 'Group',
            price: {
                discount_price: '$2,199.00',
                price: '$2,599.00',
            },
            reviews: 42
        },
        {
            src: [
                'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1531803769430-6b87a02636ab?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1542224566-6e85f2e6772f?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1920&h=1200&fit=crop',
                'https://images.unsplash.com/photo-1589182373726-e4f658ab50f0?w=1920&h=1200&fit=crop'
            ],
            alt: [
                'Patagonia Torres del Paine Mountains',
                'Perito Moreno Glacier in El Calafate',
                'El Chaltén Hiking Trails and Mount Fitz Roy',
                'Patagonian Wildlife - Guanacos',
                'Torres del Paine National Park Lakes'
            ],
            type: 'Group Tour',
            imageCount: 55,
            videoCount: 4,
            title: 'Patagonia Explorer - Glaciers, Mountains & Wildlife',
            transferType: 'Bus',
            start: 'Buenos Aires',
            stops: 'El Calafate, El Chaltén, Torres del Paine',
            end: 'Santiago',
            tourDuration: '10 Days',
            tag: 'Private',
            price: {
                discount_price: '$1,799.00',
                price: '$2,099.00',
            },
            reviews: 27
        }
    ];

    // Demo içerik yalnızca GrapesJS blok önizlemesinde kullanılabilir; üretim anasayfasında
    // API/CMS verisi yoksa gerçek olmayan tur kartları göstermeyiz.
    const toursToShow = (serverTours && serverTours.length > 0)
        ? serverTours
        : (!hasServerData && shouldEmbedBlockPropsInDom() ? featuredToursFallback : []);
    const showEmptyState = !isLoading && toursToShow.length === 0;
    /** xl’de slidesPerView=4; yeterli slayt yoksa loop Swiper ölçülerini bozar */
    const loopEnabled = toursToShow.length > 4;

    if (isLoading) {
        return (
            <section className="relative z-50 bg-[url(/static/map.png)] bg-position-[-100px_0px] bg-no-repeat mt-[160px] bg-size-[770px_630px]">
                <div className="pb-[200px] opacity-100">
                    <div className="w-6/12 mx-auto">
                        <SectionTitle title={displayTitle} titleTag="h2" subTitle={displaySubTitle} subTitleTag="span" description={null} descriptionTag={null} />
                    </div>
                    <div className="relative w-full overflow-x-hidden px-2 sm:px-4 md:px-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                            {Array.from({ length: displayLimit }, (_, i) => (
                                <TourCardSkeleton key={i} index={i} viewType="box" />
                            ))}
                        </div>
                        <div className="flex justify-center mt-8">
                            <div className="h-12 w-40 bg-gray-200 rounded-lg animate-pulse" />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (showEmptyState) {
        return (
            <section className="relative z-50 bg-[url(/static/map.png)] bg-position-[-100px_0px] bg-no-repeat mt-[160px] bg-size-[770px_630px]">
                <div className="w-6/12 mx-auto text-center pb-[200px]">
                    <SectionTitle title={displayTitle} titleTag="h2" subTitle={displaySubTitle} subTitleTag="span" description={null} descriptionTag={null} />
                    <p className="text-gray-500 mt-4">{getVariable('tour.no_tours_in_category', 'Seçilen kategoride tur bulunamadı.')}</p>
                </div>
            </section>
        );
    }

    return (
        <section className="relative z-50 bg-[url(/static/map.png)] bg-position-[-100px_0px] bg-no-repeat mt-[160px] bg-size-[770px_630px]">
            <div className="pb-[200px] opacity-100">
                <div className="w-6/12 mx-auto">
                    <SectionTitle title={displayTitle} titleTag="h2" subTitle={displaySubTitle} subTitleTag="span" description={null} descriptionTag={null} />
                </div>
                <div className="relative w-full overflow-x-hidden">
                    <div className="flex flex-row">
                        <div className="hidden md:flex w-2/12 px-4 lg:px-6 xl:px-10 justify-end items-center">
                            <button type="button"
                                onClick={() => swiperInstance?.slidePrev()}
                                className="group p-3 sm:p-4 md:p-5 lg:p-[20px] border border-alsero-dark-gray rounded-xl sm:rounded-2xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all ease-in-out cursor-pointer duration-300"
                                aria-label="Previous slide"
                            >
                                <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray group-hover:fill-white">
                                    <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z"/>
                                </svg>
                            </button>
                        </div>
                        <div className="w-full md:w-10/12 px-2 sm:px-4 md:px-6 lg:px-0 mx-auto">
                            <Swiper
                                modules={[Navigation]}
                                spaceBetween={16}
                                slidesPerView={1}
                                loop={loopEnabled}
                                watchOverflow
                                observer
                                observeParents
                                onSwiper={(swiper) => setSwiperInstance(swiper)}
                                breakpoints={{
                                    640: {
                                        slidesPerView: 2,
                                        spaceBetween: 16,
                                    },
                                    // 768: 3 kart dar kalıyor — tablet’te 2
                                    768: {
                                        slidesPerView: 2,
                                        spaceBetween: 20,
                                    },
                                    1024: {
                                        slidesPerView: 3,
                                        spaceBetween: 20,
                                    },
                                    1280: {
                                        slidesPerView: 4,
                                        spaceBetween: 16,
                                    },
                                }}
                                className="w-full h-full !overflow-hidden"
                            >
                                {toursToShow.map((tour, index) => (
                                    <SwiperSlide key={tour.tourId || tour.slug || index} className="!h-auto py-4 sm:py-6 md:py-8 lg:py-10 px-2 sm:px-3 md:px-4">
                                        <TourCard 
                                            tour={tour} 
                                            viewType="box" 
                                            index={index}
                                            languageId={languageId}
                                            language={resolvedLanguageCode}
                                            languageCode={resolvedLanguageCode}
                                            pageUrls={pageUrls}
                                            defaultLanguage={defaultLanguage}
                                            productTypeForUrl="private-tours"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                        <div className="hidden md:flex w-2/12 px-4 lg:px-6 xl:px-10 justify-start items-center">
                            <button type="button"
                                onClick={() => swiperInstance?.slideNext()}
                                className="group p-3 sm:p-4 md:p-5 lg:p-[20px] border border-alsero-dark-gray rounded-xl sm:rounded-2xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all ease-in-out cursor-pointer duration-300"
                                aria-label="Next slide"
                            >
                                <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray group-hover:fill-white">
                                    <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <Link href={displayViewAllUrl} className="bg-alsero-first flex flex-row gap-2 items-center px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-lg font-sans text-white text-sm sm:text-base">
                            VIEW ALL TOUR
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.00049 -3.11466e-09C3.14056 -1.39718e-09 0.000488283 3.14007 0.000488284 7C0.000488286 10.8599 3.14056 14 7.00049 14C10.8604 14 14.0005 10.8599 14.0005 7C14.0005 3.14007 10.8604 -4.83214e-09 7.00049 -3.11466e-09ZM9.16291 7.41243L6.24624 10.3291C6.13249 10.4428 5.98316 10.5 5.83381 10.5C5.68449 10.5 5.53514 10.4428 5.42139 10.3291C5.19331 10.101 5.19331 9.73232 5.42139 9.50425C6.80445 8.1212 6.80447 5.87881 5.42141 4.49575C5.19334 4.26768 5.19334 3.899 5.42141 3.67093C5.64949 3.44285 6.01816 3.44285 6.24624 3.67093L9.16291 6.5876C9.39099 6.81568 9.39099 7.18432 9.16291 7.41243Z" fill="white"/>
                            </svg>

                        </Link>
                    </div>
                </div>
            </div>
        </section>
    )
}

